import { List, Map, OrderedMap, Record, Set } from 'immutable';
import { RecordCellSet } from '@/domain/journal/helper';
import { NOTE_PALETTE_DEFAULT_KEY } from '@/components/LabeledThemeProvider/notesPalette';

function createViewInfo() {
  return {
    pages: 0,
    rotations: new List(),
  };
}

export const ViewinfoFactory = new Record(createViewInfo());

export const ParamsFactory = new Record({
  category: null,
  creationDate: null,
  subject: null,
  uploadText: null,
  ccEmailList: null,
  linkID: null,
  linkToDocument: null,
});

export const UploadQueueItemFactory = new Record({
  id: '',
  path: '',
  status: 'pending',
  progress: 0,
  cancelToken: null,
  file: null,
  tags: null,
  params: ParamsFactory(),
  isConfidential: false,
});

export const CachedDocumentFactory = new Record({
  hash: null,
  downloadProgress: 0,
});

export const LinkedFactory = new Record({
  count: 0,
  pageToken: null,
  tag: null,
  list: new Map(),
  isOpen: false,
});

export const ApprovalStampFactory = new Record({
  ll: null, // low left coordinates
  ur: null, // up right coordinates
  rotation: null,
});

export const DocumentFactory = new Record({
  documentID: '',
  created: '',
  favorite: false,
  isAcceptable: false,
  doc: '',
  docStatus: '',
  finInfo: null,
  sourceID: null,
  versionID: null,
  folderID: null,
  new: 0,
  notes: '',
  notesColor: NOTE_PALETTE_DEFAULT_KEY,
  source: '',
  total: 0,
  type: 2,
  tags: new Set(),
  doctype: 'general',
  status: ['new'],
  reason: '',
  protected: false,
  viewinfo: ViewinfoFactory(),
  linkid: '',
  ltext: '',
  rootCategory: null,
  isConfidential: false,
  approvals: new Map(),
  gridMeta: new Map(),
  fromEmail: false,
  indicateWarning: false,
  multipleDocuments: false,
  stateColumn: null,
  canBeMoved: false,
  creator: '',
  sign: {
    signPosition: ApprovalStampFactory(),
  },
});

export const ValidatedDocumentFactory = new Record({
  documentID: '',
  created: '',
  doc: '',
  docStatus: '',
  finInfo: null,
  sourceID: null,
  versionID: null,
  folderID: null,
  new: 0,
  notes: '',
  notesColor: NOTE_PALETTE_DEFAULT_KEY,
  source: '',
  total: 0,
  type: 2,
  tags: new Set(),
  doctype: 'general',
  status: ['new'],
  reason: '',
  protected: false,
  viewinfo: ViewinfoFactory(),
  linkid: '',
  ltext: '',
  acceptValidationError: null,
  rootCategory: null,
  isConfidential: false,
  approvals: new Map(),
  gridMeta: new Map(),
  fromEmail: false,
  indicateWarning: false,
  multipleDocuments: false,
  lastModifiedDate: '',
  approvalsActiveGroupId: null,
  canBeMoved: false,
  creator: '',
  sign: {
    signPosition: ApprovalStampFactory(),
  },
});

export const HotkeyFactory = new Record({
  keys: new List(),
  description: '',
  level: '',
});

export const HotkeyCellListFactory = new Record({
  set: RecordCellSet(),
  name: '',
  cell: '',
});

export const HotkeyMatchFactory = new Record({
  key: new List(),
  cell_list: new List(),
});

export const HotkeysStateFactory = new Record({
  isLoading: false,
  companyId: null,
  data: new List(),
});

const SupplierPageTokensFactory = new Record({
  received: null,
  in_process: null,
  processed: null,
  paid: null,
  rejected: null,
});

const SupplierStateDocCountsFactory = new Record({
  received: 0,
  in_process: 0,
  processed: 0,
  paid: 0,
  rejected: 0,
});

const SupplierStateDocCountsWithoutLinkPanelsFactory = new Record({
  received: 0,
  in_process: 0,
  processed: 0,
  paid: 0,
  rejected: 0,
});

export const GridApplyTransactionFactory = new Record({
  nodes: new List(), // TODO: recheck default value and type
  transactionType: null,
});

export const DocumentsFactory = new Record({
  list: new OrderedMap(),
  document: ValidatedDocumentFactory(),
  gridHeadersList: new List(),
  gridDocumentsByPageIndex: new Map(), // used for navigate on document page between documents from grid workspace
  editableDoc: ValidatedDocumentFactory(),
  linked: LinkedFactory(),
  linkingTag: '',
  pageToken: '',
  companyId: '',
  searchTags: [],
  uploadQueue: new Map(),
  erpDuplicated: new Set(),
  searchQuery: '',
  count: 0,
  processing: 0,
  company_total: 0,
  count_without_link_panels: 0,
  fetchedDocumentsTimeStamp: null,
  latestWorkerTimeStamp: null,
  gridLoader: false,
  // DA-19430: search_new `aggregations` block (doctype tabs + present sub-types) — rides the
  // worker/search response; see schemas.ts.
  aggregations: null, // SearchAggregations | null
  subtypeFilter: null, // string | null — transient Inbox sub-type filter (reset on navigation, not URL)
  gridSearchAfterByPageIndex: new Map(),
  gridApplyTransaction: GridApplyTransactionFactory(), // marker for invoke grid api applyServerSideTransaction
  hotkeys: HotkeysStateFactory(),
  stateColumn: null,
  supplierPageTokens: SupplierPageTokensFactory(),
  supplierStateDocCounts: SupplierStateDocCountsFactory(),
  supplierStateDocCountsWithoutLinkPanels: SupplierStateDocCountsWithoutLinkPanelsFactory(),
  cachedDocuments: new List(),
  currentViewers: new List(),
  justPublished: null,
  isPublishNavigationPending: false,
  loaded: false,
  shouldInvalidateDocumentCache: false,
});
